import { CommerceProperty } from '@msdyn365-commerce/retail-proxy';

/**
 * Gets a string extension property value by name
 * @param extensionProperties - Array of commerce properties
 * @param propertyName - Name of the property to retrieve
 * @param defaultValue - Default value if property not found
 * @returns The string value or default
 */
export function getExtensionPropertyString(
    extensionProperties: CommerceProperty[] | undefined,
    propertyName: string,
    defaultValue: string = ''
): string {
    if (!extensionProperties || !extensionProperties.find || extensionProperties.length === 0) {
        return defaultValue;
    }

    const property = extensionProperties.find(prop => prop.Key?.toLowerCase() === propertyName.toLowerCase());

    if (!property?.Value?.StringValue) {
        return defaultValue;
    }

    return property.Value.StringValue;
}

/**
 * Gets a number extension property value by name
 * @param extensionProperties - Array of commerce properties
 * @param propertyName - Name of the property to retrieve
 * @param defaultValue - Default value if property not found
 * @returns The number value or default
 */
export function getExtensionPropertyNumber(
    extensionProperties: CommerceProperty[] | undefined,
    propertyName: string,
    defaultValue: number = 0
): number {
    if (!extensionProperties || !extensionProperties.find || extensionProperties.length === 0) {
        return defaultValue;
    }

    const property = extensionProperties.find(prop => prop.Key?.toLowerCase() === propertyName.toLowerCase());

    if (property?.Value?.DecimalValue !== undefined && property.Value.DecimalValue !== null) {
        return property.Value.DecimalValue;
    }

    if (property?.Value?.IntegerValue !== undefined && property.Value.IntegerValue !== null) {
        return property.Value.IntegerValue;
    }

    if (property?.Value?.StringValue) {
        const parsed = parseFloat(property.Value.StringValue);
        if (!isNaN(parsed)) {
            return parsed;
        }
    }

    return defaultValue;
}

/**
 * Gets a boolean extension property value by name
 * @param extensionProperties - Array of commerce properties
 * @param propertyName - Name of the property to retrieve
 * @param defaultValue - Default value if property not found
 * @returns The boolean value or default
 */
export function getExtensionPropertyBoolean(
    extensionProperties: CommerceProperty[] | undefined,
    propertyName: string,
    defaultValue: boolean = false
): boolean {
    if (!extensionProperties || !extensionProperties.find || extensionProperties.length === 0) {
        return defaultValue;
    }

    const property = extensionProperties.find(prop => prop.Key?.toLowerCase() === propertyName.toLowerCase());

    if (property?.Value?.BooleanValue !== undefined && property.Value.BooleanValue !== null) {
        return property.Value.BooleanValue;
    }

    if (property?.Value?.StringValue) {
        const stringValue = property.Value.StringValue.toLowerCase();
        return stringValue === 'true' || stringValue === '1' || stringValue === 'yes';
    }

    return defaultValue;
}

/**
 * Gets a date extension property value by name
 * @param extensionProperties - Array of commerce properties
 * @param propertyName - Name of the property to retrieve
 * @param defaultValue - Default value if property not found
 * @returns The Date value or default
 */
export function getExtensionPropertyDate(
    extensionProperties: CommerceProperty[] | undefined,
    propertyName: string,
    defaultValue: Date | null = null
): Date | null {
    if (!extensionProperties || !extensionProperties.find || extensionProperties.length === 0) {
        return defaultValue;
    }

    const property = extensionProperties.find(prop => prop.Key?.toLowerCase() === propertyName.toLowerCase());

    if (property?.Value?.DateTimeOffsetValue) {
        return new Date(property.Value.DateTimeOffsetValue);
    }

    if (property?.Value?.StringValue) {
        const parsed = new Date(property.Value.StringValue);
        if (!isNaN(parsed.getTime())) {
            return parsed;
        }
    }

    return defaultValue;
}

/**
 * Checks if an extension property exists
 * @param extensionProperties - Array of commerce properties
 * @param propertyName - Name of the property to check
 * @returns True if property exists
 */
export function hasExtensionProperty(extensionProperties: CommerceProperty[] | undefined, propertyName: string): boolean {
    if (!extensionProperties || !extensionProperties.some || extensionProperties.length === 0) {
        return false;
    }

    return extensionProperties.some(prop => prop.Key?.toLowerCase() === propertyName.toLowerCase());
}

/**
 * Gets all extension property keys
 * @param extensionProperties - Array of commerce properties
 * @returns Array of property keys
 */
export function getExtensionPropertyKeys(extensionProperties: CommerceProperty[] | undefined): string[] {
    if (!extensionProperties || !extensionProperties.map || extensionProperties.length === 0) {
        return [];
    }

    return extensionProperties.map(prop => prop.Key).filter((key): key is string => !!key);
}

/**
 * Legacy class-based API for backward compatibility
 * @deprecated Use individual functions instead
 */
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class ExtensionPropertyUtil {
    public static getString = getExtensionPropertyString;
    public static getNumber = getExtensionPropertyNumber;
    public static getBoolean = getExtensionPropertyBoolean;
    public static getDate = getExtensionPropertyDate;
    public static hasProperty = hasExtensionProperty;
    public static getPropertyKeys = getExtensionPropertyKeys;
}
